import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Plus, Trash, PaperPlaneTilt, Calendar, Users, ChartBar
} from '@phosphor-icons/react';

export default function CmsPushNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [form, setForm] = useState({
    title: '',
    body: '',
    icon: '',
    url: '/',
    target_audience: 'all',
    scheduled_at: '',
  });

  useEffect(() => {
    loadNotifications();
    loadStats();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/push-notifications`, {
        withCredentials: true,
      });
      setNotifications(r.data.notifications || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/push-notifications/stats/overview`, {
        withCredentials: true,
      });
      setStats(r.data);
    } catch (e) {
      console.error('Failed to load stats');
    }
  };

  const createNotification = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/push-notifications`,
        form,
        { withCredentials: true }
      );
      toast.success(form.scheduled_at ? 'Notification scheduled' : 'Notification sent');
      setForm({
        title: '',
        body: '',
        icon: '',
        url: '/',
        target_audience: 'all',
        scheduled_at: '',
      });
      setShowComposer(false);
      loadNotifications();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create notification');
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/push-notifications/${notificationId}`,
        { withCredentials: true }
      );
      toast.success('Notification deleted');
      loadNotifications();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-push-notifications">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Bell size={28} weight="duotone" className="text-primary" />
          Push Notifications
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Send browser push notifications to users with scheduling and targeting
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Sent</p>
            <p className="text-2xl font-bold">{stats.total_notifications}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Delivered</p>
            <p className="text-2xl font-bold">{stats.total_sent_count}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Clicks</p>
            <p className="text-2xl font-bold">{stats.total_clicks}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">CTR</p>
            <p className="text-2xl font-bold">{stats.click_through_rate}%</p>
          </div>
        </div>
      )}

      {/* Composer */}
      {showComposer ? (
        <form onSubmit={createNotification} className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">New Notification</h3>
            <button
              type="button"
              onClick={() => setShowComposer(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={100}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              data-testid="notif-title"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Body *</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              maxLength={300}
              rows={3}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              data-testid="notif-body"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Target Audience</label>
              <select
                value={form.target_audience}
                onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              >
                <option value="all">All Users</option>
                <option value="writers">Writers</option>
                <option value="readers">Readers</option>
                <option value="premium">Premium Users</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Click URL</label>
              <input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Schedule (optional)</label>
            <input
              type="datetime-local"
              value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
            />
          </div>

          <button
            type="submit"
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold"
            data-testid="send-notif-btn"
          >
            {form.scheduled_at ? 'Schedule Notification' : 'Send Now'}
          </button>
        </form>
      ) : (
        <button
          onClick={() => setShowComposer(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold"
          data-testid="new-notif-btn"
        >
          <Plus size={16} /> New Notification
        </button>
      )}

      {/* Notifications List */}
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : notifications.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Bell size={48} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No notifications sent yet</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {notifications.map((notif) => (
            <div key={notif.notification_id} className="p-4" data-testid={`notif-${notif.notification_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold">{notif.title}</p>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                      notif.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {notif.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{notif.body}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="capitalize">{notif.target_audience}</span>
                    <span>·</span>
                    <span>{notif.sent_count} sent</span>
                    <span>·</span>
                    <span>{notif.click_count} clicks</span>
                    <span>·</span>
                    <span>{new Date(notif.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <button
                  onClick={() => deleteNotification(notif.notification_id)}
                  className="text-muted-foreground hover:text-red-600 p-1"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
